import { useLayoutEffect, useState } from 'react';
import styled from 'styled-components';
import { Cell } from '@/shared/ui/Cell';
import { GOALS_MAP } from '@/widgets/Goals';
import { useUserStore } from '@/entities/User';
import { useModalStore } from '@/entities/Modal';
import { Typography } from '@/shared/ui/Typography';
import { RangeInput } from '@/shared/ui/RangeInput';
import { Button, Select } from '@salutejs/sdds-insol';
import { ModalWrapper } from '@/shared/ui/ModalWrapper';
import { useGlobalStore } from '@/entities/GlobalStore';
import { useUpdateGoal } from '../query/useUpdateGoal';
import { MAX_SLIDER_VALUE, MIN_SLIDER_VALUE, RANGE_ERROR, SELECT_ERROR, SELECT_ITEMS } from '../model/data';
import cls from './GoalModal.module.scss';

const StyledButton = styled(Button)`
    background-color: #333f48;
`;

export type SelectedGoalParams = {
    initialRoomsCount: number;
    initialSquareMeters: number;
};

export const GoalModal = () => {
    const {
        selectedGoalParams: { initialSquareMeters, initialRoomsCount },
        setSelectedGoalParams,
    } = useGlobalStore();

    const {
        user: { userId },
    } = useUserStore();

    const { isGoalModalOpen, setIsGoalModalOpen } = useModalStore();

    const { mutate: updateGoal, isPending: isUpdating } = useUpdateGoal();

    const [metersValue, setMetersValue] = useState(MIN_SLIDER_VALUE);
    const [selectedRoomsCount, setSelectedRoomsCount] = useState<string | undefined>(undefined);
    const [selectError, setSelectError] = useState<string>('');
    const [rangeError, setRangeError] = useState<string>('');

    useLayoutEffect(() => {
        setMetersValue(initialSquareMeters || MIN_SLIDER_VALUE);
    }, [initialSquareMeters]);

    useLayoutEffect(() => {
        setSelectedRoomsCount(initialRoomsCount ? String(initialRoomsCount) : undefined);
    }, [initialRoomsCount]);

    const selectItems = SELECT_ITEMS.map((item) => ({
        ...item,
        disabled: item.value === selectedRoomsCount,
    }));

    const selectedRoom = GOALS_MAP.get(Number(selectedRoomsCount))?.schemeImage;

    const onCloseModal = () => {
        setIsGoalModalOpen(false);
        setSelectedGoalParams({} as SelectedGoalParams);
    };

    const validateRange = () => {
        if (metersValue < MIN_SLIDER_VALUE || metersValue > MAX_SLIDER_VALUE) {
            setRangeError(RANGE_ERROR);
        }
    };

    const handleSubmit = () => {
        if (selectError || rangeError) {
            return;
        }

        let hasError = false;

        if (metersValue < MIN_SLIDER_VALUE || metersValue > MAX_SLIDER_VALUE) {
            setRangeError(RANGE_ERROR);
            hasError = true;
        }

        if (!selectedRoomsCount) {
            setSelectError(SELECT_ERROR);
            hasError = true;
        }

        if (hasError) {
            return;
        }

        updateGoal({ userId, goalMeters: metersValue, goalRooms: Number(selectedRoomsCount) });
    };

    return (
        <ModalWrapper id="goalModal" isOpened={isGoalModalOpen} setIsOpened={onCloseModal}>
            <div className={cls.container}>
                <Typography as="h6" variant="h6_bold">
                    Выбор параметров квартиры
                </Typography>
                <div className={cls.form}>
                    <Cell errorClassName={cls.rangeError} error={rangeError}>
                        <RangeInput
                            symbol="м²"
                            maxLength={3}
                            value={metersValue}
                            min={MIN_SLIDER_VALUE}
                            max={MAX_SLIDER_VALUE}
                            placeholder="Выберите количество м²"
                            hideFieldSymbol
                            onBlur={validateRange}
                            onChange={(value) => {
                                setRangeError('');
                                setMetersValue(value);
                            }}
                        />
                    </Cell>
                    <Cell errorClassName={cls.selectError} error={selectError}>
                        <Select
                            renderItem={({ label }) => {
                                return (
                                    <Typography as="p" variant="text_16">
                                        {label}
                                    </Typography>
                                );
                            }}
                            size="l"
                            items={selectItems}
                            labelPlacement="inner"
                            label="Количество комнат"
                            value={selectedRoomsCount}
                            onChange={(value) => {
                                setSelectedRoomsCount(value);
                                setSelectError('');
                            }}
                        />
                    </Cell>
                </div>
                {selectedRoom && (
                    <div className={cls.image}>
                        <img src={selectedRoom} alt="selectedRoom" />
                    </div>
                )}
                <StyledButton
                    className={cls.button}
                    size="m"
                    view="dark"
                    type="submit"
                    disabled={isUpdating}
                    isLoading={isUpdating}
                    stretch
                    onClick={() => {
                        handleSubmit();
                    }}
                >
                    <span className="SBSansText">Сохранить цель</span>
                </StyledButton>
            </div>
        </ModalWrapper>
    );
};
